<script lang="ts">
    import {exists} from 'shared/utils/custom-utils';
    import {createEventDispatcher} from 'svelte';
    import telephonePrefixesJson from './telephone_prefixes.json';
    import KpDropdownMenuButton from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';
    import type {
        TelephoneNumberPrefix,
        TelephoneNumberPrefixChange
    } from 'shared/value-editors/internal/editors/telephone-number/types';

    export let phoneNumberValue: string;
    const telephonePrefixes: TelephoneNumberPrefix[] = telephonePrefixesJson;
    const dispatch = createEventDispatcher<{ 'change': TelephoneNumberPrefixChange }>();

    const czechTelephonePrefix = telephonePrefixes.find((prefix) => prefix.code === 'CZ');

    // Export for two-way binding
    export let currentPrefix: TelephoneNumberPrefix | null = null;
    $: currentPrefix = extractPrefixFromValue(phoneNumberValue) ?? czechTelephonePrefix;

    function extractPrefixFromValue(value: string): TelephoneNumberPrefix | null {
        if (!exists(value) || !value.startsWith('+')) {
            return null;
        }

        for (let i = 3; i >= 1; i--) {
            const code = value.slice(0, i + 1);
            const foundPrefix = telephonePrefixes.find((prefix) => prefix.dialCode === code);

            if (exists(foundPrefix)) {
                return foundPrefix;
            }
        }

        return null;
    }

    const handlePrefixClick = (prefix: TelephoneNumberPrefix) => {
        dispatch('change', {
            oldPrefix: currentPrefix?.dialCode,
            newPrefix: prefix.dialCode
        });
    }
</script>

<div class="telephone-prefixes-dropdown-container">
    <KpDropdownMenuButton buttonSize="xs" buttonStyle="link" additionalButtonClasses="telephone-prefixes-dropdown-btn">
        <svelte:fragment slot="button">
            <IconedContent trailingIcon="angle-small-down" gap="0.5ch">
                <img class="flag-img"
                     src={currentPrefix.flagUrl}
                     alt={currentPrefix.code}
                     loading="lazy"/>

                <span class="localized-name">
                    {currentPrefix.localizedName}
                </span>

                <span class="country-code">
                    {currentPrefix.code}
                </span>
            </IconedContent>
        </svelte:fragment>

        <svelte:fragment slot="menu">
            {#each telephonePrefixes as prefix(prefix.code)}
                <KpDropdownMenuItem on:click={() => handlePrefixClick(prefix)}>
                    <img class="flag-img"
                         slot="icon"
                         src={prefix.flagUrl}
                         alt={prefix.code}
                         loading="lazy"/>

                    {prefix.name} ({prefix.dialCode})
                </KpDropdownMenuItem>
            {/each}
        </svelte:fragment>
    </KpDropdownMenuButton>
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    :global {
        .telephone-prefixes-dropdown-container {
            .telephone-prefixes-dropdown-btn {
                text-decoration: none;
                padding: 0;
                color: inherit;

                &:hover {
                    text-decoration: none;
                }
            }
        }
    }

    .country-code {
        display: none;
    }

    @media (max-width: @screen-xs-max) {
        .country-code {
            display: inline;
        }

        .localized-name {
            display: none;
        }
    }

    .flag-img {
        width: 18px;
        overflow: hidden;
        border-radius: 2px;
        margin-right: 2px;
        border: 1px solid @table-border-color;
    }
</style>